// src/components/NoSignalBar.jsx
// The navy bar at the bottom of the screen that says something opened is still loading, or waits for
// the signal (09/16/2026, fourth review round of the phone speed work). App.js draws it outside its
// loading screen, on every staff screen but never on a client's portal.
//
// Why a bar and not only the loading screen's note: with no signal a page file waits for the signal
// (utils/lazyPage.js), but React Router runs every move between pages as a transition, and React then
// keeps the old page up while the new one waits. No loading screen appears, the tap looks ignored, and
// the page turns up by itself later. Pop-up forms such as Turn In wait behind an empty loading screen
// and show nothing at all. So whenever a page someone opened is still loading, this bar says so.
//
// One message at a time (fifth review round, same day, follows lazyPage's two phases):
//   1. the loading screen's own note (components/ScreenLoading.jsx), when it is on screen: this bar then
//      shows nothing, since that note already says it;
//   2. otherwise, while anything opened waits for a usable connection: "No signal. Screens you open will
//      load...". It no longer promises the screen itself, which the person may have left since;
//   3. otherwise, while anything opened is being tried with the signal back: "Loading what you
//      opened...", left out while a loading screen already says "Loading...";
//   4. otherwise, App's note that Continue with Google waits for the connection (App.js decides when;
//      this bar only draws it), in the same place it always had.
// All of them share this one bar, so they can never sit on top of each other.
//
// A save that waits (10/05/2026, utils/saveWhenConnected.js): a checklist tick that failed for a
// connection reason is kept and tried again once the signal is back. While it waits the bar says so
// first, before any of the lines above: a change of the person's own matters more than a screen
// still loading, and the line does not repeat "No signal", so it reads fine beside the loading
// screen's own note. While a save is being tried (again, or for more than a moment) it says
// "Saving your change...".
//
// And never two in one painted frame: when this bar is first drawn together with a loading screen, that
// screen reports its note only after this bar has drawn, and this bar would otherwise hear of it only
// after the browser had painted. So after every draw the bar checks what it drew against the latest
// counts, and draws again at once if they differ, which React does before the screen is painted.
//
// Part of the file every launch downloads, so it imports only React and two files that download
// already holds.
import { useLayoutEffect, useReducer, useSyncExternalStore } from "react";
import { subscribeWaitingPages, waitingPagePhase } from "../utils/lazyPage";
import { subscribeWaitingSaves, waitingSavePhase } from "../utils/saveWhenConnected";
import { loadingScreensOnScreen, signalNotesOnScreen, subscribeSignalNotes } from "./ScreenLoading";

// Honest about where the change is kept: in this tab, until it saves.
export const SAVE_WAITS_MESSAGE = "Your change will save when your signal comes back. Keep this tab open.";
export const SAVE_TRYING_MESSAGE = "Saving your change...";
export const PAGE_WAITS_MESSAGE = "No signal. Screens you open will load when your signal comes back.";
export const PAGE_LOADING_MESSAGE = "Loading what you opened...";
export const GOOGLE_WAITS_MESSAGE =
  "Google sign-in needs a connection and will be ready when your signal comes back.";

export default function NoSignalBar({ googleWaits = false }) {
  const phase = useSyncExternalStore(subscribeWaitingPages, waitingPagePhase);
  const savePhase = useSyncExternalStore(subscribeWaitingSaves, waitingSavePhase);
  const notesOnScreen = useSyncExternalStore(subscribeSignalNotes, signalNotesOnScreen);
  const screensUp = useSyncExternalStore(subscribeSignalNotes, loadingScreensOnScreen);
  const [, drawAgain] = useReducer((n) => n + 1, 0);

  useLayoutEffect(() => {
    if (
      waitingPagePhase() !== phase ||
      waitingSavePhase() !== savePhase ||
      signalNotesOnScreen() !== notesOnScreen ||
      loadingScreensOnScreen() !== screensUp
    ) {
      drawAgain();
    }
  });

  let message = null;
  if (savePhase === "signal") message = SAVE_WAITS_MESSAGE;
  else if (savePhase === "trying") message = SAVE_TRYING_MESSAGE;
  else if (notesOnScreen > 0) message = null;
  else if (phase === "signal") message = PAGE_WAITS_MESSAGE;
  else if (phase === "trying") message = screensUp > 0 ? null : PAGE_LOADING_MESSAGE;
  else if (googleWaits) message = GOOGLE_WAITS_MESSAGE;
  if (!message) return null;
  // Pinned to the bottom, clear of the New Version bar at the top and of the iPhone's home bar.
  return (
    <p
      role="status"
      className="fixed bottom-0 inset-x-0 z-[90] bg-[#1E293B] text-white text-sm text-center px-4 pt-2 print:hidden"
      style={{ paddingBottom: "max(0.5rem, var(--tes-safe-bottom))" }}
    >
      {message}
    </p>
  );
}
